import {
    collection,
    component,
    EntityData,
    field,
    identifier,
    localDateField,
    localDateTimeField,
    LocalisableEntityData,
    timeField,
    transientCollection,
    transientField
} from "@fedeltapointofsale/data-client";
import {TypedTerminalReferenceData} from "../../pos/terminal/terminal-reference.data";
import {DateTimeUtil} from "../../framework/utils/date-time-util";
import {MembershipTypeSearchModel} from "../../customers/membership-type/membership-type-search.model";

export enum PeriodScheduleRuleOption {
    TIME_OF_DAY_RANGE,
    SINGLE_DATE,
    DATE_RANGE,
    DATE_TIME_RANGE,
    DAYS_OF_WEEK,
    MONTHS,
    DAY_OF_MONTH_RANGE,
    PERIOD_SCHEDULES,
    TERMINALS,
    EXCL_TERMINALS,
    MEMBERSHIP_TYPES,
    EXCL_MEMBERSHIP_TYPES,
    TIME_ZONES
}

export class PeriodScheduleReferenceData extends LocalisableEntityData<PeriodScheduleReferenceData> {
    @identifier id: number;
    @field name: string;
    @field description;
}

export class PeriodScheduleRuleData extends EntityData<PeriodScheduleRuleData> {
    @identifier ruleId: string;
    @localDateTimeField startLocalDateTime: Date;
    @localDateTimeField endLocalDateTime: Date;
    @localDateField startLocalDate: Date;
    @localDateField endLocalDate: Date;
    @localDateField singleLocalDate: Date;
    @collection() months: string[] = [];
    @collection() daysOfWeek: string[] = [];
    @field startDayOfMonth: number;
    @field endDayOfMonth: number;
    @timeField("HH:mm") startTimeOfDay: Date;
    @timeField("HH:mm") endTimeOfDay: Date;
    @collection(PeriodScheduleReferenceData) anyRefPeriodSchedules: PeriodScheduleReferenceData[] = [];
    @collection(PeriodScheduleReferenceData) allRefPeriodSchedules: PeriodScheduleReferenceData[] = [];
    @collection(PeriodScheduleReferenceData) noneRefPeriodSchedules: PeriodScheduleReferenceData[] = [];
    @collection() timeZones: string[] = [];
    @collection(TypedTerminalReferenceData) terminals: TypedTerminalReferenceData[] = [];
    @collection(TypedTerminalReferenceData) exclTerminals: TypedTerminalReferenceData[] = [];
    @field membershipTypeIds: number[] = [];
    @field exclMembershipTypeIds: number[] = [];
    @field includeGuests: boolean;
    @field excludeGuests: boolean;

    @transientCollection() selectedOptions: PeriodScheduleRuleOption[] = [];

    optionSelected(ruleOption: PeriodScheduleRuleOption): boolean {
        return this.selectedOptions.indexOf(ruleOption) >= 0;
    }

    removeOption(ruleOption: PeriodScheduleRuleOption): void {
        this.selectedOptions = this.selectedOptions.filter(o => o != ruleOption);
    }

    @transientField get startLocalDTDate(): Date {
        return this.startLocalDateTime;
    }

    set startLocalDTDate(start: Date) {
        this.startLocalDateTime = DateTimeUtil.setDate(this.startLocalDateTime, start);
    }

    @transientField get startLocalDTTime(): Date {
        return this.startLocalDateTime;
    }

    set startLocalDTTime(start: Date) {
        this.startLocalDateTime = DateTimeUtil.setTime(this.startLocalDateTime, start);
    }

    @transientField get endLocalDTDate(): Date {
        return this.endLocalDateTime;
    }

    set endLocalDTDate(finish: Date) {
        this.endLocalDateTime = DateTimeUtil.setDate(this.endLocalDateTime, finish);
    }

    @transientField get endLocalDTTime(): Date {
        return this.endLocalDateTime;
    }

    set endLocalDTTime(finish: Date) {
        this.endLocalDateTime = DateTimeUtil.setTime(this.endLocalDateTime, finish);
    }

    initSelectedOptions(): void {
        if (this.timeZones.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.TIME_ZONES)
        }
        if (this.terminals.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.TERMINALS)
        } else if (this.exclTerminals.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.EXCL_TERMINALS)
        }
        if (this.singleLocalDate) {
            this.selectedOptions.push(PeriodScheduleRuleOption.SINGLE_DATE)
        } else if (this.startLocalDate || this.endLocalDate) {
            this.selectedOptions.push(PeriodScheduleRuleOption.DATE_RANGE)
        } else if (this.startLocalDateTime || this.endLocalDateTime) {
            this.selectedOptions.push(PeriodScheduleRuleOption.DATE_TIME_RANGE)
        }
        if (this.daysOfWeek.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.DAYS_OF_WEEK)
        }
        if (this.months.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.MONTHS)
        }
        if (this.startTimeOfDay || this.endTimeOfDay) {
            this.selectedOptions.push(PeriodScheduleRuleOption.TIME_OF_DAY_RANGE)
        }
        if (this.startDayOfMonth || this.endDayOfMonth) {
            this.selectedOptions.push(PeriodScheduleRuleOption.DAY_OF_MONTH_RANGE)
        }
        if (this.anyRefPeriodSchedules.length || this.allRefPeriodSchedules.length || this.noneRefPeriodSchedules.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.PERIOD_SCHEDULES)
        }
        if (this.includeGuests) {
            this.membershipTypeIds.unshift(MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
        } else if (this.excludeGuests) {
            this.exclMembershipTypeIds.unshift(MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
        }
        if (this.membershipTypeIds.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.MEMBERSHIP_TYPES)
        } else if (this.exclMembershipTypeIds.length) {
            this.selectedOptions.push(PeriodScheduleRuleOption.EXCL_MEMBERSHIP_TYPES)
        }
    }

    get hasCriteria(): boolean {
        return !!this.singleLocalDate ||
            !!this.startLocalDate || !!this.endLocalDate ||
            !!this.startLocalDateTime || !!this.endLocalDateTime ||
            !!this.months.length ||
            !!this.daysOfWeek.length ||
            !!this.startDayOfMonth || !!this.endDayOfMonth ||
            !!this.startTimeOfDay || !!this.endTimeOfDay ||
            !!this.anyRefPeriodSchedules.length || !!this.allRefPeriodSchedules.length || !!this.noneRefPeriodSchedules.length ||
            !!this.timeZones.length ||
            !!this.terminals.length || !!this.exclTerminals.length ||
            !!this.membershipTypeIds.length || !!this.exclMembershipTypeIds.length;
    }

    clearUnselectedCriteria(): void {
        if (!this.optionSelected(PeriodScheduleRuleOption.TIME_ZONES)) {
            this.timeZones = [];
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.TERMINALS)) {
            this.terminals = [];
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.EXCL_TERMINALS)) {
            this.exclTerminals = [];
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.SINGLE_DATE)) {
            this.singleLocalDate = null;
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.DATE_RANGE)) {
            this.startLocalDate = null;
            this.endLocalDate = null;
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.DATE_TIME_RANGE)) {
            this.startLocalDateTime = null;
            this.endLocalDateTime = null;
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.DAYS_OF_WEEK)) {
            this.daysOfWeek = [];
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.MONTHS)) {
            this.months = [];
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.DAY_OF_MONTH_RANGE)) {
            this.startDayOfMonth = null;
            this.endDayOfMonth = null;
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.TIME_OF_DAY_RANGE)) {
            this.startTimeOfDay = null;
            this.endTimeOfDay = null;
        }
        if (!this.optionSelected(PeriodScheduleRuleOption.PERIOD_SCHEDULES)) {
            this.anyRefPeriodSchedules = [];
            this.allRefPeriodSchedules = [];
            this.noneRefPeriodSchedules = [];
        }
        if (this.optionSelected(PeriodScheduleRuleOption.MEMBERSHIP_TYPES)) {
            this.includeGuests = !!this.membershipTypeIds.find((membershipTypeId: number) => membershipTypeId === MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
            this.membershipTypeIds = this.membershipTypeIds.filter((membershipTypeId: number) => membershipTypeId !== MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
        } else {
            this.membershipTypeIds = [];
            this.includeGuests = false;
        }
        if (this.optionSelected(PeriodScheduleRuleOption.EXCL_MEMBERSHIP_TYPES)) {
            this.excludeGuests = !!this.exclMembershipTypeIds.find((membershipTypeId: number) => membershipTypeId === MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
            this.exclMembershipTypeIds = this.exclMembershipTypeIds.filter((membershipTypeId: number) => membershipTypeId !== MembershipTypeSearchModel.GUEST_MEMBERSHIP_TYPE);
        } else {
            this.exclMembershipTypeIds = [];
            this.excludeGuests = false;
        }
    }
}

export class PeriodScheduleData extends LocalisableEntityData<PeriodScheduleData> {
    @identifier id: number;
    @field name: string;
    @field description: string;
    @field enabled: boolean = true;
    @field showAsVenueStatus: boolean = false;
    @field venueStatusLabel: string;
    @field showOnlyWhenScheduled: boolean = false;
    @field venueStatusSwitchable: boolean = false;
    @field venueStatusDefault: boolean = true;
    @field resetAtStartOfDay: boolean = true;
    @component overallRule: PeriodScheduleRuleData;
    @collection(PeriodScheduleRuleData) activeRules: PeriodScheduleRuleData[];
    @collection(PeriodScheduleRuleData) inactiveRules: PeriodScheduleRuleData[];
}
